// `ResetPasswordPage` — US-006.
//
// DESIGN.md §1.6, §4.6, O9 (same 4-segment strength meter as signup).

import { useId, useMemo, useState } from 'react';

import { ApiError, useResetPassword } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, Field, Input } from '@barel/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type SubmitHandler } from 'react-hook-form';

import { AuthLayout } from './AuthLayout';
import { PasswordField } from './PasswordField';
import { PasswordStrengthMeter } from './components/PasswordStrengthMeter';
import { fieldError } from './lib/localizeError';
import { resetPasswordFormSchema, type ResetPasswordFormInput } from './lib/schemas';
import { emit } from './lib/telemetry';
import { isPasswordTooWeak, usePasswordStrength } from './lib/usePasswordStrength';

interface ResetSearch {
  token?: string;
}

const REDIRECT_DELAY_MS = 1_200;

export function ResetPasswordPage() {
  const t = useT('auth');
  const navigate = useNavigate();
  const reset = useResetPassword();
  const search = useSearch({ strict: false }) as ResetSearch;

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormInput>({
    resolver: zodResolver(resetPasswordFormSchema),
    mode: 'onBlur',
    defaultValues: {
      token: search.token ?? '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPassword = watch('newPassword') ?? '';
  const userInputs = useMemo(() => [], []);
  const strength = usePasswordStrength(newPassword, userInputs);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [bannerError, setBannerError] = useState<{
    tone: 'error' | 'warning';
    message: string;
    cta?: { label: string; to: '/auth/forgot' | '/auth/login' };
  } | null>(null);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const tokenMissing = !search.token;

  const onSubmit: SubmitHandler<ResetPasswordFormInput> = async (values) => {
    setBannerError(null);
    emit({ name: 'auth_reset_submitted' });

    if (isPasswordTooWeak(strength.score)) {
      setError('newPassword', { message: t('signUp.errors.weakPassword') });
      return;
    }

    try {
      await reset.mutateAsync({
        token: search.token ?? values.token,
        newPassword: values.newPassword,
      });
      emit({ name: 'auth_reset_succeeded' });
      setShowSuccess(true);
      setTimeout(() => {
        void navigate({
          to: '/auth/login',
          search: { reset: '1' },
          replace: true,
        });
      }, REDIRECT_DELAY_MS);
    } catch (err) {
      if (ApiError.is(err)) {
        if (err.code === 'verification.token_expired') {
          setBannerError({
            tone: 'error',
            message: t('resetPassword.complete.errors.expired'),
            cta: { label: t('verify.error.requestNewCta'), to: '/auth/forgot' },
          });
          return;
        }
        if (err.code === 'verification.token_invalid') {
          setBannerError({
            tone: 'error',
            message: t('resetPassword.complete.errors.invalid'),
            cta: { label: t('verify.error.requestNewCta'), to: '/auth/forgot' },
          });
          return;
        }
      }
      setBannerError({ tone: 'error', message: t('resetPassword.complete.errors.generic') });
    }
  };

  const titleId = useId();

  if (tokenMissing) {
    return (
      <AuthLayout brandHeadline="brandHeadlineNeutral">
        <section data-route="auth/reset" className="flex flex-col gap-6">
          <h1 className="text-xl font-semibold text-primary">
            {t('resetPassword.complete.title')}
          </h1>
          <Banner tone="error">{t('resetPassword.complete.errors.missingToken')}</Banner>
          <Button asChild variant="primary" size="lg" fullWidth>
            <Link to="/auth/forgot">{t('verify.error.requestNewCta')}</Link>
          </Button>
        </section>
      </AuthLayout>
    );
  }

  const newPasswordError = fieldError(
    errors.newPassword,
    t('fieldErrors.password.tooShort', { min: 10 }),
  );
  const confirmPasswordError = errors.confirmPassword
    ? errors.confirmPassword.message === 'confirmMismatch'
      ? t('resetPassword.complete.errors.confirmMismatch')
      : (errors.confirmPassword.message ?? t('resetPassword.complete.errors.confirmMismatch'))
    : undefined;

  return (
    <AuthLayout brandHeadline="brandHeadlineNeutral">
      <section data-route="auth/reset" aria-labelledby={titleId} className="flex flex-col gap-6">
        <header className="flex flex-col gap-1">
          <h1 id={titleId} className="text-xl font-semibold text-primary">
            {t('resetPassword.complete.title')}
          </h1>
          <p className="text-sm text-secondary">{t('resetPassword.complete.subtitle')}</p>
        </header>

        {showSuccess ? (
          <Banner tone="success" data-testid="reset-success-banner">
            {t('resetPassword.complete.successBody')}
          </Banner>
        ) : null}

        {bannerError ? (
          <div className="flex flex-col gap-3">
            <Banner tone={bannerError.tone}>{bannerError.message}</Banner>
            {bannerError.cta ? (
              <Button asChild variant="primary" size="lg" fullWidth>
                <Link to={bannerError.cta.to}>{bannerError.cta.label}</Link>
              </Button>
            ) : null}
          </div>
        ) : null}

        {!showSuccess ? (
          <form
            aria-busy={isSubmitting || undefined}
            className="flex flex-col gap-4"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            data-testid="reset-form"
          >
            <input type="hidden" {...register('token')} value={search.token ?? ''} />

            <Field invalid={!!newPasswordError} required>
              <Field.Label>{t('resetPassword.complete.fields.newPassword.label')}</Field.Label>
              <Field.Control>
                <PasswordField
                  {...register('newPassword')}
                  autoComplete="new-password"
                  data-testid="reset-new-password"
                  toggleShowLabel={t('resetPassword.complete.fields.newPassword.toggleShow')}
                  toggleHideLabel={t('resetPassword.complete.fields.newPassword.toggleHide')}
                  shown={showPassword}
                  onToggleShown={() => setShowPassword((v) => !v)}
                />
              </Field.Control>
              {newPasswordError ? <Field.Error>{newPasswordError}</Field.Error> : null}
            </Field>

            {newPassword.length > 0 ? (
              <PasswordStrengthMeter score={strength.score} ready={strength.ready} />
            ) : null}

            <Field invalid={!!confirmPasswordError} required>
              <Field.Label>{t('resetPassword.complete.fields.confirmPassword.label')}</Field.Label>
              <Field.Control>
                <Input
                  {...register('confirmPassword')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  data-testid="reset-confirm-password"
                />
              </Field.Control>
              {confirmPasswordError ? <Field.Error>{confirmPasswordError}</Field.Error> : null}
            </Field>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={isSubmitting}
              data-testid="reset-submit"
            >
              {t('resetPassword.complete.submit')}
            </Button>

            <Link to="/auth/login" className="text-sm text-secondary underline hover:text-primary">
              {t('resetPassword.request.backToSignIn')}
            </Link>
          </form>
        ) : null}
      </section>
    </AuthLayout>
  );
}
